import { z } from 'zod';

export const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginFormData = z.infer<typeof loginSchema>;

const createPasswordSchema = (label: string) =>
  z.string().superRefine((value, ctx) => {
    if (value.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${label} is required` });
    } else if (value.length < 8) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} must be at least 8 characters`,
      });
    } else if (value.length > 20) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${label} must not exceed 20 characters`,
      });
    }
  });

export const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, 'Old password is required'),
    newPassword: createPasswordSchema('New password'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export const forgotPasswordSchema = z
  .object({
    mobile: z.string().min(1, 'Mobile number is required'),
    otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit OTP sent to your mobile'),
    newPassword: createPasswordSchema('New password'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const registerSchema = z.object({
  firstName: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (value.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'First name is required' });
      } else if (value.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'First name must be at least 3 characters',
        });
      } else if (value.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'First name must not exceed 50 characters',
        });
      } else if (!/^[a-zA-Z]+$/.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'First name must contain only letters',
        });
      }
    }),
  lastName: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (value.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Last name is required' });
      } else if (value.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Last name must be at least 3 characters',
        });
      } else if (value.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Last name must not exceed 50 characters',
        });
      } else if (!/^[a-zA-Z]+$/.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Last name must contain only letters',
        });
      }
    }),
  username: z
    .string()
    .trim()
    .superRefine((value, ctx) => {
      if (value.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Username is required' });
      } else if (value.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Username must be at least 3 characters',
        });
      } else if (value.length > 50) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Username must not exceed 50 characters',
        });
      } else if (!/^[a-zA-Z0-9]+$/.test(value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Username can only contain letters and numbers',
        });
      }
    }),
  password: createPasswordSchema('Password'),
  phone: z.string().min(8, 'Enter a valid phone number'),
  otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit OTP sent to your phone'),
  referralCode: z.string().optional(),
});

export type RegisterFormData = z.infer<typeof registerSchema>;

export const stakeSchema = z.object({
  stakes: z.array(
    z.object({
      label: z
        .string()
        .trim()
        .min(1, 'Label is required')
        .max(8, 'Label must not exceed 8 characters')
        .regex(/^[a-zA-Z0-9]+$/, 'Label must be alphanumeric'),

      value: z
        .number()
        .min(1, 'Value must be greater than 0')
        .max(500000, 'Value must not be greater than 500000'),
    })
  ),
});

export type StakeFormValues = z.infer<typeof stakeSchema>;

export const accountSchema = z.object({
  accountType: z.enum(['savings', 'current'], {
    errorMap: () => ({ message: 'Select account type' }),
  }),
  bankName: z.string().min(2, 'Bank name is required').max(50, 'Too long'),

  accountHolderName: z.string().min(2, 'Account holder name is required').max(50, 'Too long'),

  accountNumber: z
    .string()
    .min(8, 'Account number too short')
    .max(18, 'Account number too long')
    .regex(/^\d+$/, 'Account number must be numeric'),

  ifsc: z
    .string()
    .length(11, 'IFSC must be 11 characters')
    .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'Invalid IFSC code'),
});

export type AccountFormValues = z.infer<typeof accountSchema>;

export const depositSchema = z.object({
  txnRef: z
    .string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'UTR is required')
    .refine((val) => !/\s/.test(val), 'UTR cannot contain spaces')
    .refine((val) => /^[a-zA-Z0-9]+$/.test(val), 'UTR must contain only letters and numbers')
    .refine((val) => /\d/.test(val), 'UTR cannot contain only letters')
    .refine((val) => val.length >= 8 && val.length <= 22, 'UTR must be between 8 and 22 characters')
    .transform((val) => val.toUpperCase()),

  amount: z.coerce.number().min(100, 'Minimum amount is 100'),

  proof: z
    .custom<File>((file) => file instanceof File, 'File is required')
    .refine((file) => file.size <= 5 * 1024 * 1024, 'Max 5MB allowed'),

  agree: z.boolean().refine((val) => val === true, 'You must accept policy'),
});

export type DepositFormValues = z.infer<typeof depositSchema>;

export const upiSchema = z.object({
  name: z.string().min(2, 'Name is required').max(50, 'Name too long'),

  upi: z
    .string()
    .min(3, 'UPI is required')
    .regex(/^[\w.-]+@[\w.-]+$/, 'Invalid UPI ID'),

  phone: z.string().regex(/^[6-9]\d{9}$/, 'Invalid phone number'),
});

export type UpiFormValues = z.infer<typeof upiSchema>;

export const createDepositSchema = (min: number, max: number) => {
  return z.discriminatedUnion('type', [
    z.object({
      type: z.literal('wallet'),
      // crypto: z.coerce.number().optional(),
      // fiat: z.coerce.number().optional(),
      amount: z.coerce
        .number()
        .min(min, `Minimum amount is ${min}`)
        .max(max, `Maximum amount is ${max}`),
      utr: z
        .string()
        .transform((val) => val.trim())
        .refine((val) => val.length > 0, 'UTR is required')
        .refine((val) => !/\s/.test(val), 'UTR cannot contain spaces')
        .refine((val) => /^[a-zA-Z0-9]+$/.test(val), 'UTR must contain only letters and numbers')
        .refine((val) => /\d/.test(val), 'UTR cannot contain only letters')
        .refine(
          (val) => val.length >= 12 && val.length <= 22,
          'UTR must be between 12 and 22 characters'
        )
        .transform((val) => val.toUpperCase()),
      paymentProof: z.string().nonempty('image is required'),
    }),
    z.object({
      type: z.literal('crypto'),
      //   amount: z.coerce.number().optional(),
      utr: z
        .string()
        .nonempty('Transaction Hash is required')
        .regex(/^[a-zA-Z0-9]+$/, 'Transaction Hash must be alphanumeric')
        .min(6, 'Transaction Hash must be at least 6 characters')
        .max(100, 'Transaction Hash cannot exceed 100 characters'),
      crypto: z.coerce.number(),
      fiat: z.coerce.number().min(min, `Minimum is ${min}`),
      paymentProof: z.string().optional(),
    }),
  ]);
};
export type DepositFormType = z.infer<ReturnType<typeof createDepositSchema>>;

export const createWithdrawAmountSchema = (min: number, max: number) =>
  z.object({
    amount: z
      .string({ required_error: 'Amount is required' })
      .min(1, 'Amount is required')
      .refine((val) => /^\d*\.?\d*$/.test(val), {
        message: 'Please enter a valid number',
      })
      .refine((val) => Number(val) >= min, {
        message: `Minimum amount is ${min.toLocaleString()}`,
      })
      .refine((val) => Number(val) <= max, {
        message: `Maximum amount is ${max.toLocaleString()}`,
      }),
  });

export type WithdrawAmountFormValues = z.infer<ReturnType<typeof createWithdrawAmountSchema>>;

export const cryptoAccountSchema = z.object({
  walletAddress: z
    .string()
    .trim()
    .nonempty('Wallet address is required')
    .min(10, 'Wallet address must be at least 10 characters')
    .max(100, 'Wallet address cannot exceed 100 characters')
    .regex(/^[a-zA-Z0-9]+$/, 'Wallet address must be alphanumeric'),
  qrImage: z.string().nonempty('QR code is required'),
});

export type CryptoAccountFormValues = z.infer<typeof cryptoAccountSchema>;
